import React from 'react'
import * as clipboard from 'clipboard-polyfill/text'
import { verbalizeMatchExplanation, TesterResult } from '@feedyou/utils'
import styled from 'styled-components'
import { modal } from '@features/imperativeModals'

import { ModalFooterButton } from '../../../components/modals/styles'

export async function showTestResults(result: TesterResult) {
  await modal(({ onClose }) => ({
    title: 'NLP test results',
    children: <TestResultsContent result={result} />,
    footer: (
      <>
        <ModalFooterButton label="Close" emphasis="secondary" onClick={() => onClose()} />
        <ModalFooterButton
          label="Copy results"
          emphasis="primary"
          onClick={() => {
            copyResults(result.results)
          }}
        />
      </>
    ),
    modalStyle: { content: { height: '90vh', maxHeight: '90vh', width: '90%', maxWidth: 1200 } },
    modalContentStyle: { height: '100%', display: 'flex', flexDirection: 'column' }
  }))
}

const TestResultsContent: React.FC<{ result: TesterResult }> = ({ result }) => {
  const counts = Object.entries(result?.stats?.countsByResult ?? {})

  return (
    <Container>
      <StatsSection>
        <StatItem>
          Overall precision: <strong>{Math.round(result.stats.precision * 10000) / 100} %</strong>
        </StatItem>
        <StatItem>
          Counts by result:
          <CountsList>
            {counts.map(([resultType, count]) => (
              <CountItem key={resultType}>
                {resultType}: <strong>{count}</strong>
              </CountItem>
            ))}
          </CountsList>
        </StatItem>
      </StatsSection>

      <TableContainer>
        <ResultsTable>
          <thead>
            <tr>
              <th>Utterance</th>
              <th>Expected</th>
              <th>Classifications</th>
              <th>Precision</th>
              <th>Result</th>
              <th>Duration</th>
            </tr>
          </thead>
          <tbody>
            {result.results.map((row, index) => (
              <tr key={index}>
                <td>{row.utterance}</td>
                <td>{row.expectedIntent.modelId + '---' + row.expectedIntent.intentId}</td>
                <td>
                  {row.classifications.map((c, i) => (
                    <div key={i}>
                      {/* The trick with explanation here is that only highest priority group is matched */}
                      {/* so we can display the global explanation at each case */}
                      {c.intent} ({c.score}, ${verbalizeMatchExplanation.mode(row.explanationRaw)})
                    </div>
                  ))}
                </td>
                <td>{Math.round(row.precision * 10000) / 100} %</td>
                <td>{row.result}</td>
                <td>{row.testCaseDurationMs} ms</td>
              </tr>
            ))}
          </tbody>
        </ResultsTable>
      </TableContainer>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 16px;
`

const StatsSection = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StatItem = styled.li`
  font-size: 14px;
  line-height: 1.5;
`

const CountsList = styled.ul`
  list-style: disc;
  padding-left: 25px;
  margin-top: 4px;
`

const CountItem = styled.li`
  font-size: 14px;
`

const TableContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
`

const ResultsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    padding: 8px 12px;
    text-align: left;
    border-bottom: 1px solid #dee2e6;
  }

  th {
    background-color: #f8f9fa;
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  tbody tr:hover {
    background-color: #f8f9fa;
  }

  td {
    vertical-align: top;
  }
`

function copyResults(results: TesterResult['results']) {
  const text = results
    .map(({ result, explanation, classifications, testCaseDurationMs }) =>
      [
        result,
        explanation,
        (classifications || []).map(classification => classification?.intent || '').join(','),
        (classifications || []).map(classification => classification?.score || '').join(','),
        testCaseDurationMs
      ].join('\t')
    )
    .join('\n')

  clipboard
    .writeText(text)
    .catch(err => console.warn('Cannot copy diagnostics info to the clipboard', err))
}
